/**
 * This file has the message formats for the different messages that are
 * received by the client from the websocket.
 *
 * Each websocket `message` contains an `event` that has a:
 *  - `type`,
 *  - `parent_type` and
 *  - `data`.
 *
 * These `events` are then reused by the `store` and `actions`.
 */

import { BidAwardStatusApi } from '@shared/datastore/api-typings/projects/projects';
import { SourceTypeApi } from '../api-typings/messages/messages_types';
import { OnlineOfflineStatusType } from '../online-offline/online-offline.model';

export type WebsocketServerEvent = SubMessageEvent | WebsocketMessage;

interface SubMessageEvent {
  channel: 'subscribe';
  body: 'OK';
}

// This models the data from pack_project() in event_notify.php
export type WebsocketProjectData = {
  id: number;
  actionText: string;
  appended_descr: string;
  imgUrl: string;
  isTest: boolean;
  jobs?: string[];
  jobString: string;
  linkUrl: string;
  showBudgetMinimallyPostedProject: boolean;
  text: string;
  time: number;
  title: string;
  userId: number;
  userName: string;
} & (
  | {
      isMinimallyPostedProject: true;
      minimallyPostedProjectBudgetSet: boolean;
    }
  | {
      isMinimallyPostedProject: false;
      minimallyPostedProjectBudgetSet: false;
    });

interface WebsocketServiceData {
  id: number;
  service_id: string | number;
  service_name: string;
  service_url: string;
  service_duration: string;
  service_cost: string | number;
  currency_sign: string;
  currency_code: string;
  service_img: string; // A URL
  time: number;
}

interface BaseServerData {
  data: { time: number };
}

export interface WebsocketArticleCommentEvent extends BaseServerData {
  type: 'articleCommentReceived';
  parent_type: 'notifications';
  data: {
    article_author: string | number; // The ID of author
    article_name: string;
    article_type: string; // Some enum?
    comment_by: string | number;
    comment_date: string;
    comment_id: string | number;
    comment: string;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    publicName: string;
    time: number;
    user: string;
  };
}

export interface WebsocketAwardEvent extends BaseServerData {
  type: 'award';
  parent_type: 'notifications';
  data: {
    acceptByTime?: number;
    actionText?: string;
    apiMessage: {
      bid: {
        amount: number;
        award_status: BidAwardStatusApi.PENDING;
        bidder_id: number;
        id: number;
        period: number;
      };
      project: {
        currency: { code: string; sign: string };
        id: number;
        owner_id: number;
        title: string;
        type: 'fixed';
      };
      users: { [id: string]: { username: string } };
    };
    appended_descr?: string;
    awardeeId?: number;
    awardeeName?: string;
    bid?: {
      bidAmount: string;
      bidDescr: null;
      bidId: number;
      bidPeriod: string;
      id: number;
      submitTime: number;
    };
    categoryName?: string;
    currencycode?: string;
    currencysign?: string;
    id: number;
    imgUrl?: string;
    isTest?: boolean;
    jobs: string[];
    jobString: string;
    linkUrl?: string;
    projIsHourly?: boolean;
    text?: string;
    time: number;
    title: string;
    userId: number;
    userName: string;
  };
}

export interface WebsocketAwardBadgeEvent extends BaseServerData {
  type: 'awardBadge';
  parent_type: 'notifications';
  data: {
    descr: string;
    id: number;
    linkUrl: string;
    name: string;
    time: number;
  };
}

export interface WebsocketAwardCreditEvent extends BaseServerData {
  type: 'awardCredit';
  parent_type: 'notifications';
  data: {
    amount: string | number;
    id: string | number;
    linkUrl: string;
    time: number;
  };
}

export interface WebsocketAwardReminderEvent extends BaseServerData {
  type: 'awardReminder';
  parent_type: 'notifications';
  data: {
    // Note, this doesn't actually have an `id`.
    imgUrl: string;
    linkUrl: string;
    name: string;
    time: number;
    userName: string;
    bid: { id: string | number };
    // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
  };
}

export interface WebsocketAwardMilestoneReminderEvent extends BaseServerData {
  type: 'awardMilestoneReminder';
  parent_type: 'notifications';
  data: {
    appended_descr: string;
    currencycode: string;
    currencyid: string | number;
    currencysign: string;
    id: string | number;
    imgUrl: string;
    // isAutomaticPayments: boolean;
    linkUrl: string;
    // projIsHourly: boolean;
    publicName?: string;
    time: number;
    title: string;
    userId: string;
    userName: string;
    // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
  };
}

export interface WebsocketAwardXpEvent extends BaseServerData {
  type: 'awardXp';
  parent_type: 'notifications';
  data: {
    amount: string | number;
    descr: string;
    game: 'xp';
    id: string | number;
    linkUrl: string;
    time: number;
  };
}

interface WebsocketBidEventProjectCount {
  all: number;
  incomplete_reviews: number;
  complete: number;
  completion_rate: number;
  reviews: number;
  earnings: number;
  incomplete: number;
}

interface WebsocketBidEventReputation {
  com: number;
  exp: number;
  hag: number;
  onb: number;
  ont: number;
  overall: number;
  pos: number;
  pro: number;
  qua: number;
}

interface WebsocketBidEventSellerRating {
  count: number;
  projectCount: {
    last12months: WebsocketBidEventProjectCount;
    last3months: WebsocketBidEventProjectCount;
    entire_history: WebsocketBidEventProjectCount;
  };
  completionRate: number;
  avg: number;
  reputation: {
    last12months: WebsocketBidEventReputation;
    last3months: WebsocketBidEventReputation;
    entire_history: WebsocketBidEventReputation;
  };
}

export interface WebsocketBidretractedEvent extends BaseServerData {
  type: 'bidretracted';
  parent_type: 'notifications';
  data: {
    bid_id: number;
    project_id: number;
    seller_id: number;
    time: number;
  };
}

export interface WebsocketBidEvent extends BaseServerData {
  type: 'bid' | 'bid_updated' | 'bid_self';
  parent_type: 'notifications';
  data: {
    accountId?: number;
    amount: string;
    award?: string;
    bidAvg?: string;
    bidCount?: number;
    bidHidden: boolean;
    bidNo?: string;
    buyerFeePerDollar?: string;
    buyerMinProjectFee?: string;
    categoryName?: string;
    currency: string;
    currencyCode: string;
    currencyId: string;
    descr: string;
    id: number;
    imgUrl: string;
    initMilestone?: number;
    linkUrl?: string;
    period: string;
    pmb: undefined;
    projectId: number;
    projIsFullTime: boolean;
    projIsHourly: boolean;
    projName: string;
    publicName?: string;
    showAwardFee: boolean;
    text?: string;
    time: number;
    title: string;
    userId: string;
    userName: string;
    userBalances?: {
      all: {
        currency: {
          code: string;
          country: string;
          exchangerate: string;
          id: string;
          is_external?: string;
          name: string;
          seq: string;
          sign: string;
        };
        balance: string;
      }[];
      relevant: number;
    };
    bid: {
      award_score?: number;
      completed?: boolean;
      completion_score?: number;
      descr_html?: string;
      descr_short_html?: string;
      descr_short?: string;
      descr?: null;
      expert_guarantee?: null;
      highlighted?: boolean;
      id: number;
      isShortlisted?: null;
      milestone_percentage?: string;
      new_ranking?: number;
      no?: string;
      paid_status?: null;
      period?: string;
      project_id?: string;
      r2?: number;
      ranking?: number;
      score?: number;
      sponsored?: null;
      submitdate_f?: string;
      submitdate_f2?: ' recently';
      submitdate_ts?: number;
      submitdate?: string;
      sum?: string;
      userId: number;
      user?: {
        address: { country: string; code: string; city: null };
        company: string;
        currency: number;
        earnings: number;
        flag_class: string;
        flag_name: string;
        flag: { name: string; icon: string };
        gold: number;
        hourlyrate: number;
        id: number;
        insignia: {
          name: string;
          title: string;
          examEnabled: string;
          examId: string;
          id: string;
          description: string;
        }[];
        isPortfolioCreated: boolean;
        jobs: string[];
        logo_Url: string;
        normalMemberclassLogo: string;
        online?: string;
        profile_logo_Url: string;
        provider_rating: { count: number; avg: string };
        publicName?: string;
        rating: { count: number; avg: string };
        reg_date: string;
        reg_unixtime: number;
        reputation_rate: number;
        timezone: number;
        tooltipMemberclassLogo: string;
        Url: string;
        username: string;
        seller_rating: WebsocketBidEventSellerRating;
      };
    };
  };
}

interface BidWebsocketApiMessage {
  bid: {
    amount: number;
    award_status: BidAwardStatusApi;
    bidder_id: number;
    id: number;
    period: number;
  };
  project: {
    currency: {
      code: string;
      id: number;
      sign: string;
    };
    id: number;
    owner_id: number;
    title: string;
  };
  users: {
    [index: number]: { username: string };
  };
}

export interface WebsocketAcceptedEvent {
  parent_type: 'notifications';
  type: 'accepted';
  data: {
    bid: {
      retracted: boolean;
      descr: string;
    };
    apiMessage: BidWebsocketApiMessage;
  };
}

export interface WebsocketRevokedEvent {
  parent_type: 'notifications';
  type: 'revoked';
  data: {
    time: number;
    apiMessage: BidWebsocketApiMessage;
    awardeeId: number;
    bidId: number;
    projectId: number;
  };
}

export interface WebsocketCancelMilestoneEvent {
  type: 'cancelMilestone';
  parent_type: 'notifications';
  data: {
    id: number;
    time: number;
    tranId: number;
  };
  targets: number[];
}

export interface WebsocketCompletedEvent extends BaseServerData {
  type: 'completed';
  parent_type: 'notifications';
  data: {
    amount: string | number;
    categoryName: string;
    completeStatus: string; // TODO: T37445 Make this a string enum
    currencycode: string;
    currencyid: string | number;
    currencysign: string;
    employerId: string | number;
    employerPublicName?: string;
    employerUsername: string;
    expiredTimestamp: string;
    freelancerId: string | number;
    freelancerPublicName?: string;
    freelancerUsername: string;
    id: string | number;
    img_e: { profile_logo_url: string }; // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
    img_f: { profile_logo_url: string }; // TODO: T37457 There's more in event_notify.php but currently the navigation only needs this.
    name: string;
    // projIsHourly: boolean;
    selectionId: string | number;
    seoUrl: string;
    state?: string; // TODO: T37445 Make this a string enum
    submitDate: string;
    time: number;
    userName: string;
  };
}

export interface WebsocketContestEvent extends BaseServerData {
  type: 'contest';
  parent_type: 'notifications';
  data: {
    buyer?: string;
    buyerUrl?: string;
    currency?: string;
    currencyCode: string;
    exchangerate: string;
    extended: boolean;
    featured: boolean;
    free_bid_until?: number;
    highlight: undefined;
    id: string | number;
    imgUrl: string;
    isTest: boolean;
    jobs: number[];
    jobString: string;
    linkUrl: string;
    locked: boolean;
    maxbudget?: string | number;
    private: boolean;
    sealed: boolean;
    submitDate: string;
    time: number;
    title: string;
    userId: string;
    userName: string;
  };
}

export interface WebsocketContestAwardedToEmployerEvent extends BaseServerData {
  type: 'contestAwardedToEmployer';
  parent_type: 'notifications';
  data: {
    contestName: string;
    contestSEOUrl: string;
    currencyCode: string;
    currencySign: string;
    entry_id: string;
    entry_number: string;
    entryCount: string;
    freelancerCount: string;
    id: string | number;
    imgUrl: string;
    isBought: boolean;
    linkUrl: string;
    prize: string | number;
    publicName?: string;
    thumb: string;
    time: number;
    userId: number;
    userName: string;
  };
}

export interface WebsocketContestAwardedToFreelancerEvent
  extends BaseServerData {
  type: 'contestAwardedToFreelancer';
  parent_type: 'notifications';
  data: {
    contestName: string;
    contestSEOURL: string;
    currencyCode: string;
    currencySign: string;
    entry_id: string | number;
    entry_number: string | number;
    entryCount: string | number;
    freelancerCount: string | number;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    prize: string | number;
    publicName?: string;
    time: number;
    userId: string | number;
    userName: string;
    // thumb: ???;
  };
}

export interface WebsocketContestEntryBoughtToEmployerEvent
  extends BaseServerData {
  type: 'contestEntryBoughtToEmployer';
  parent_type: 'notifications';
  data: {
    contestName: string;
    entry_id: string | number;
    entry_number: string | number;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    publicName?: string;
    time: number;
    userName: string;
  };
}

export interface WebsocketContestEntryBoughtToFreelancerEvent
  extends BaseServerData {
  type: 'contestEntryBoughtToFreelancer';
  parent_type: 'notifications';
  data: {
    contestName: string;
    entry_id: string | number;
    entry_number: string | number;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    publicName?: string;
    time: number;
    userName: string;
  };
}

export interface WebsocketContestEntryEvent extends BaseServerData {
  type: 'contestEntry';
  parent_type: 'notifications';
  data: {
    contestHolder: string;
    contestId: string;
    contestName: string;
    firstUser?: string;
    firstUserPublicName?: string;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    secondUser?: string;
    secondUserPublicName?: string;
    time: number;
    userId: number;
  };
}

export interface WebsocketContestEntryRatedToFreelancerEvent
  extends BaseServerData {
  type: 'contestEntryRatedToFreelancer';
  parent_type: 'notifications';
  data: {
    action: string;
    contestId: string;
    contestName: string;
    employerId: string;
    entriesUrl: string;
    entryId: string;
    entryNumber: string;
    entryS3path: string;
    entryThumb: string;
    entryUrl: string;
    freelancerId: string;
    hasOtherEntriesRatedOrRejected: boolean;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    ratedOrRejectedEntriesCount: number;
    rating: string;
    time: number;
  };
}

export interface WebsocketContestEntryRejectedToFreelancerEvent
  extends BaseServerData {
  type: 'contestEntryRejectedToFreelancer';
  parent_type: 'notifications';
  data: {
    action: string;
    contestId: string;
    contestName: string;
    employerId: string;
    entriesUrl: string;
    entryId: string;
    entryNumber: string;
    entryS3path: string;
    entryThumb: string;
    entryUrl: string;
    freelancerId: string;
    hasOtherEntriesRatedOrRejected: boolean;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    ratedOrRejectedEntriesCount: number;
    rating: string;
    time: number;
  };
}

export interface WebsocketContestEntryReconsideredToFreelancerEvent
  extends BaseServerData {
  type: 'contestEntryReconsideredToFreelancer';
  parent_type: 'notifications';
  data: {
    action: string;
    contestId: string;
    contestName: string;
    employerId: string;
    entriesUrl: string;
    entryId: string;
    entryNumber: string;
    entryS3path: string;
    entryThumb: string;
    entryUrl: string;
    freelancerId: string;
    hasOtherEntriesRatedOrRejected: boolean;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    ratedOrRejectedEntriesCount: number;
    rating: string;
    time: number;
  };
}

export interface WebsocketContestEntryCommentedToFreelancerEvent
  extends BaseServerData {
  type: 'contestEntryCommentedToFreelancer';
  parent_type: 'notifications';
  data: {
    contestId: string;
    contestName: string;
    entryNumber: string;
    id: string;
    imgUrl: string;
    linkUrl: string;
    time: number;
  };
}

export interface WebsocketContestExpiredEvent extends BaseServerData {
  type: 'contestExpired';
  parent_type: 'notifications';
  data: {
    id: string | number;
    time: number;
    // ???
  };
}

export interface WebsocketContestPCBNotificationEvent extends BaseServerData {
  type: 'contestPCBNotification';
  parent_type: 'notifications';
  data: {
    contestHolder: string;
    contestId: string;
    contestName: string;
    firstUser?: string;
    firstUserPublicName?: string;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    secondUser?: string;
    secondUserPublicName?: string;
    time: number;
    userId: number;
  };
}

export interface WebsocketContestPCBNotificationFullViewEvent
  extends BaseServerData {
  type: 'contestPCBNotificationFullView';
  parent_type: 'notifications';
  data: {
    contestHolder: string | number;
    contestName: string;
    entryNumber: string | number;
    firstUser: string;
    firstUserPublicName?: string;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    secondUser?: string;
    secondUserPublicName?: string;
    time: number;
    userId: string | number;
  };
}

export interface WebsocketCreateMilestoneEvent extends BaseServerData {
  type: 'createMilestone';
  parent_type: 'notifications';
  data: WebsocketProjectData & {
    account_id: string | number;
    amount: string | number;
    bidId: string | number;
    currencycode: string;
    currencysign: string;
    project_id: string | number;
    publicName?: string;
    shortDescr: string;
    time: number;
    title: string;
    transId: string | number;
    id: string | number;
    userId: string;
    // newMilestone: ???;
  };
}

export interface WebsocketDenyedEvent extends BaseServerData {
  type: 'denyed';
  parent_type: 'notifications';
  data: {
    apiMessage: {
      project: {
        id: number;
        title: string;
        owner_id: number;
        currency: {
          sign: string;
          code: string;
        };
      };
      bid: {
        bidder_id: number;
        award_status: 'rejected';
      };
      users: {
        [userId: string]: { username: string };
      };
    };
    autoRepostProjectId?: string | number;
    autoRepostProjectName?: string;
    bidId: string | number;
    currencycode: string;
    currencyId: string | number;
    currencysign: string;
    id: string | number;
    // img: ???;
    imgUrl: string;
    jobsURL: string;
    linkUrl: string;
    name: string;
    projectType: string; // TODO: T37445 Make this a string enum
    projIsHourly: any;
    publicName?: string;
    reason: string;
    seoUrl: string;
    time: number;
    userId: string | number;
    userName: string;
  };
}

export interface WebsocketDraftContestEvent extends BaseServerData {
  type: 'draftContest';
  parent_type: 'notifications';
  data: {
    contestEditUrl?: string;
    contestId: string;
    contestName: string;
    contestUrl: string;
    id: string | number;
    imgUrl: string;
    isPublished: boolean;
    time: number;
  };
}

export interface WebsocketFailingProjectEvent extends BaseServerData {
  type: 'failingProject';
  parent_type: 'notifications';
  data: WebsocketProjectData & {
    address?: string;
    currency?: string;
    currencyCode: string;
    exchangerate: string;
    featured: boolean;
    free_bid_until?: number;
    fulltime: boolean;
    hideBids: boolean;
    id: string | number;
    isLocal: boolean;
    maxbudget?: number;
    minbudget?: number;
    nonpublic: boolean;
    projectFreeBid: boolean;
    projIsHourly: boolean;
    publicName?: string;
    submitDate: string;
    time: number;
    xpValue: number;
  };
}

export interface WebsocketHighLtvAnnualDiscountEvent extends BaseServerData {
  type: 'highLtvAnnualDiscount';
  parent_type: 'notifications';
  data: {
    id: string | number;
    time: number;
    userDisplayName: string;
    linkUrl: string;
  };
}

export interface WebsocketHireMeEvent extends BaseServerData {
  type: 'hireMe';
  parent_type: 'notifications';
  // NOTE: doesn't have isTest, so can't use WebsocketProjectData
  data: {
    // acceptByTime: $acceptByTime;
    // accountId: $project->getAccountId();
    appended_descr: string;
    // bid: $myData['bid'];
    currency?: { code: string; sign: string };
    // currencyCode: string;
    // currencyId: string | number;
    // currencySign: string;
    // featured: $project->isFeatured();
    id: string | number;
    // img: $img;
    imgUrl: string;
    jobString: string;
    linkUrl: string;
    // name: string;
    period?: number;
    projIsHourly: boolean;
    publicName?: string;
    // seoUrl: $project->getSeoUrl();
    sum?: string | number;
    time: number;
    title: string;
    userId: number;
    userName: string;
  };
}

export interface WebsocketInviteUserBidEvent extends BaseServerData {
  type: 'inviteUserBid';
  parent_type: 'notifications';
  data: WebsocketProjectData & {
    id: string | number;
    publicName?: string;
    inviteeName: string;
    inviteeId: string;
    minBudget: number;
    maxBudget: number;
    currencysign: string;
    currencycode: string;
    // projIsHourly: boolean;
  };
}

export interface WebsocketInviteToContestEvent extends BaseServerData {
  type: 'inviteToContest';
  parent_type: 'notifications';
  data: {
    buyer: string | number; // The ID
    buyerName: string;
    buyerPublicName?: string;
    buyerUrl: string;
    contest_recommended_skills: string;
    currencycode: string;
    currencysign: string;
    description: string;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    name: string;
    prize: string | number;
    sellerId: string | number;
    sellerName: string;
    sellerPublicName?: string;
    time: number;
    title: string;
    type: string;
  };
}

export interface WebsocketInvoiceRequestedEvent extends BaseServerData {
  type: 'invoiceRequested';
  parent_type: 'notifications';
  data: {
    accountId: string | number;
    amount: string | number;
    bidId: string | number;
    currencyCode: string;
    currencySign: string;
    dueDate: string;
    id: string | number;
    // img: ???;
    imgUrl: string;
    // invoiceVars: ???;
    invoiceId: string | number;
    linkUrl: string;
    name: string;
    // projIsHourly: boolean;
    publicName?: string;
    sellerId: string | number;
    seoUrl: string;
    state?: string; // TODO: T37445 Make this a string enum
    time: number;
    userId: string | number;
    userName: string;
  };
}

export interface WebsocketLevelUpEvent extends BaseServerData {
  type: 'levelup';
  parent_type: 'notifications';
  data: {
    id: string | number;
    level: string;
    membership: boolean;
    perks: {
      description: string;
      item: boolean;
      magnitude: number;
      name?: 'BidRefreshRate' | 'item';
      symbol: string;
    }[];
    time: number;
    username: string;
    linkUrl: string;
    game: 'level';
  };
}

export interface WebsocketLocalJobPostedEvent extends BaseServerData {
  // Note: this is actually an extension of WebsocketProjectEvent's data.
  type: 'localJobPosted';
  parent_type: 'notifications';
  data: WebsocketProjectData & {
    address?: string;
    currency?: string;
    currencyCode: string;
    exchangerate: string;
    featured: boolean;
    free_bid_until?: number;
    fulltime: boolean;
    hideBids: boolean;
    isLocal: true;
    maxbudget?: number;
    minbudget?: number;
    nonpublic: boolean;
    projIsHourly: boolean;
    submitDate: string;
    publicName?: string;
  };
}

export interface WebsocketNotifyFollowerEvent extends BaseServerData {
  type: 'notifyfollower';
  parent_type: 'notifications';
  data: WebsocketProjectData & {
    id: string | number;
  };
}

export interface WebsocketProjectRecruiterEvent extends BaseServerData {
  type: 'projectRecruiter';
  parent_type: 'notifications';
  data: {
    agent: {
      users_id: string | number;
      username: string;
    };
    id: string | number;
    message: string;
    project: {
      name: string;
      id: string | number;
    };
    time: number;
  };
}

export interface WebsocketProjectEvent extends BaseServerData {
  type: 'project';
  parent_type: 'notifications';
  data: WebsocketProjectData & {
    address?: string;
    currency?: string;
    currencyCode: string;
    exchangerate: string;
    featured: boolean;
    free_bid_until?: number;
    fulltime: boolean;
    hideBids: boolean;
    isLocal: boolean;
    maxbudget?: number;
    minbudget?: number;
    nonpublic: boolean;
    projIsHourly: boolean;
    submitDate: string;
    publicName?: string;
  };
}

export interface WebsocketQuickHireProjectEvent extends BaseServerData {
  type: 'quickHireProject';
  parent_type: 'notifications';
  data: WebsocketProjectData & {
    id: string | number;
    bidderId: string | number;
    bidderName: string;
    currency?: { code: string; sign: string };
    // featured: boolean; // TODO: T37447 Are these boolean or something different?
    // fulltime: boolean; // TODO: T37447 Are these boolean or something different?
    // hideBids: boolean; // TODO: T37447 Are these boolean or something different?
    // nonpublic: boolean; // TODO: T37447 Are these boolean or something different?
    period?: number;
    projIsHourly: boolean;
    publicName?: string;
    submitDate: string;
    sum?: string | number;
  };
}

export interface WebsocketReleaseMilestoneEvent extends BaseServerData {
  type: 'releaseMilestone';
  parent_type: 'notifications';
  data: {
    accountId: string | number;
    amount: string | number;
    currencycode: string;
    currencysign: string;
    id: string | number;
    // img: ???;
    imgUrl: string;
    linkUrl: string;
    name: string;
    otherReason: string;
    publicName?: string;
    seoUrl: string;
    submitDate: string;
    time: number;
    title: string;
    userId: string;
    userName: string;
  };
}

export interface WebsocketRequestEndProjectEvent extends BaseServerData {
  type: 'requestEndProject';
  parent_type: 'notifications';
  data: {
    accountId: string | number;
    bidId: string | number;
    categoryName: string;
    dayCount: string | number;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    name: string;
    publicName?: string;
    seoUrl: string;
    submitDate: string;
    time: number;
    userId: string | number;
    userName: string;
  };
}

export interface WebsocketRequestMilestoneEvent extends BaseServerData {
  type: 'requestMilestone';
  parent_type: 'notifications';
  data: {
    accountId: string | number;
    amount: string | number;
    bidId: string;
    currencyCode: string;
    currencyId: string | number;
    currencySign: string;
    description: string;
    id: string | number;
    // img: ???;
    imgUrl: string;
    linkUrl: string;
    name: string;
    publicName?: string;
    requestId: string | number;
    sellerId: string | number;
    seoUrl: string;
    state?: string; // TODO: T37445 Make this a string enum
    submitDate: string;
    time: number;
    userId: string;
    userName: string;
  };
}

export interface WebsocketRequestToReleaseEvent extends BaseServerData {
  type: 'requestToRelease';
  parent_type: 'notifications';
  data: {
    accountId: string | number;
    amount: string | number;
    bidId: string | number;
    currencycode: string;
    currencysign: string;
    descr: string;
    id: string | number;
    // img: ???;
    imgUrl: string;
    linkUrl: string;
    name: string;
    publicName?: string;
    seoUrl: string;
    state?: string; // TODO: T37445 Make this a string enum
    time: number;
    tranId: string | number;
    userId: string | number;
    userName: string;
  };
}

export interface WebsocketServiceApprovedEvent extends BaseServerData {
  type: 'serviceApproved';
  parent_type: 'notifications';
  data: WebsocketServiceData;
}

export interface WebsocketServiceProjectEndingSellerEvent
  extends BaseServerData {
  type: 'serviceProjectEndingSeller';
  parent_type: 'notifications';
  data: {
    bid_id: string | number;
    buyer_id: string | number;
    buyer_name: string;
    hours_remaining: number;
    id: string | number;
    order_start_date: string;
    project_id: string | number;
    project_name: string;
    project_url: string;
    seller_id: string | number;
    seller_name: string;
    service_id: string | number;
    service_name: string;
    time: number;
  };
}

export interface WebsocketServiceProjectPostedSellerEvent
  extends BaseServerData {
  type: 'serviceProjectPostedSeller';
  parent_type: 'notifications';
  data: {
    bid_id: string | number;
    buyer_id: string | number;
    buyer_img_url: string;
    buyer_name: string;
    buyer_public_name?: string;
    currency_code: string;
    currency_sign: string;
    id: string | number;
    order_end_date: string;
    order_start_date: string;
    project_cost: string | number;
    // project_duration: ???;
    project_id: string | number;
    // project_milestones: ???;
    project_name: string;
    project_url: string;
    seller_id: string | number;
    seller_img_url: string;
    seller_name: string;
    seller_public_name: string;
    service_id: string | number;
    service_name: string;
    service_url: string;
    time: number;
  };
}

export interface WebsocketServiceStatusChangedEvent extends BaseServerData {
  type: 'serviceStatusChanged';
  parent_type: 'notifications';
  data: {
    id: string | number;
    time: number;
    // ???
  };
}

export interface WebsocketServiceRejectedEvent extends BaseServerData {
  type: 'serviceRejected';
  parent_type: 'notifications';
  data: WebsocketServiceData;
}

export interface WebsocketSignUpFreeTrialUpsellEvent extends BaseServerData {
  type: 'signUpFreeTrialUpsell';
  parent_type: 'notifications';
  data: {
    id: string | number;
    linkUrl: string;
    time: number;
    trialPackageName: string;
  };
}

export interface WebsocketUpsellCrowdsourceFindingEvent extends BaseServerData {
  type: 'upsellCrowdsourceFinding';
  parent_type: 'notifications';
  data: {
    id: string | number;
    project_name: string;
    time: number;
  };
}

export interface WebsocketXpContestEvent extends BaseServerData {
  type: 'xpContest';
  parent_type: 'notifications';
  data: {
    id: string | number;
    currency: string;
    currencyCode: string;
    imgUrl: string;
    isTest: boolean;
    jobString: string;
    linkUrl: string;
    maxbudget: number;
    time: number;
    title: string;
    userName: string;
  };
}

// Events that are there but we're ignoring.
// TODO: T38861 for bidHidden. It's an AB test.
export interface WebsocketDeprecatedEvent extends BaseServerData {
  type:
    | 'contactRequestSent'
    | 'contactRequestReceived'
    | 'bidHidden'
    | 'sitesBinCreated'
    | 'sitesHandOverMessageSent'
    | 'sitesListingStatusChanged'
    | 'sitesMessageSent'
    | 'sitesOfferCreated';
  parent_type: 'notifications';
  data: never;
}

type WebsocketNotificationEvent =
  | WebsocketArticleCommentEvent
  | WebsocketAwardEvent
  | WebsocketAwardBadgeEvent
  | WebsocketAwardCreditEvent
  | WebsocketAwardMilestoneReminderEvent
  | WebsocketAwardReminderEvent
  | WebsocketAwardXpEvent
  | WebsocketBidretractedEvent
  | WebsocketBidEvent
  | WebsocketCompletedEvent
  | WebsocketContestEvent
  | WebsocketContestAwardedToEmployerEvent
  | WebsocketContestAwardedToFreelancerEvent
  | WebsocketContestEntryEvent
  | WebsocketContestEntryBoughtToEmployerEvent
  | WebsocketContestEntryBoughtToFreelancerEvent
  | WebsocketContestExpiredEvent
  | WebsocketContestPCBNotificationEvent
  | WebsocketContestPCBNotificationFullViewEvent
  | WebsocketCreateMilestoneEvent
  | WebsocketAcceptedEvent
  | WebsocketRevokedEvent
  | WebsocketCancelMilestoneEvent
  | WebsocketDenyedEvent
  | WebsocketDraftContestEvent
  | WebsocketFailingProjectEvent
  | WebsocketHighLtvAnnualDiscountEvent
  | WebsocketHireMeEvent
  | WebsocketInviteToContestEvent
  | WebsocketInviteUserBidEvent
  | WebsocketInvoiceRequestedEvent
  | WebsocketLevelUpEvent
  | WebsocketLocalJobPostedEvent
  | WebsocketNotifyFollowerEvent
  | WebsocketProjectEvent
  | WebsocketProjectRecruiterEvent
  | WebsocketQuickHireProjectEvent
  | WebsocketReleaseMilestoneEvent
  | WebsocketRequestEndProjectEvent
  | WebsocketRequestMilestoneEvent
  | WebsocketRequestToReleaseEvent
  | WebsocketServiceApprovedEvent
  | WebsocketServiceProjectEndingSellerEvent
  | WebsocketServiceProjectPostedSellerEvent
  | WebsocketServiceStatusChangedEvent
  | WebsocketServiceRejectedEvent
  | WebsocketSignUpFreeTrialUpsellEvent
  | WebsocketUpsellCrowdsourceFindingEvent
  | WebsocketXpContestEvent
  | WebsocketDeprecatedEvent;

interface WebsocketOnlineOfflineEvent {
  type: 'statusget';
  data: { [userId: string]: OnlineOfflineStatusType };
  parent_type: 'onlineoffline';
}

export interface WebsocketGroupChatEvent extends BaseServerData {
  type: 'groupchat';
  parent_type: 'notifications';
  data: {
    action: 'add' | 'remove';
    payload: { members: number[]; thread: number };
    time: number;
  };
}

export type VideoEventType =
  | 'video:request'
  | 'video:request:audio_only'
  | 'video:close'
  | 'video:accept'
  | 'video:reject';

export interface WebsocketVideoEvent {
  type: VideoEventType;
  parent_type: 'messages';
  data: {
    session_id: string;
    thread_id: number;
    user_id: number;
  };
}

export interface WebsocketNewMessageEvent extends BaseServerData {
  type: 'private';
  parent_type: 'messages';
  data: {
    attachments: string[];
    client_message_id?: string;
    from_user: number;
    id: number;
    message: string;
    message_source: SourceTypeApi;
    parent_id: number;
    thread_id: number;
    time: number;
    time_created: number;
  };
}

export interface WebsocketTypingEvent {
  type: 'typing';
  parent_type: 'messages';
  data: {
    from_user: number;
    thread: number;
  };
}

export interface WebsocketUserReadEvent extends BaseServerData {
  type: 'user_read';
  parent_type: 'messages';
  data: {
    id: string | number;
    thread_ids: number[];
    user_id: number;
    time: number;
  };
}

export interface WebsocketUserAttachEvent extends BaseServerData {
  type: 'attach';
  parent_type: 'messages';
  data: {
    filenames: string[];
    from_user: number;
    message_id: number;
    thread_id: number;
    time: number;
  };
  id: string | number;
}

type WebsocketMessagingEvent =
  | WebsocketGroupChatEvent
  | WebsocketNewMessageEvent
  | WebsocketTypingEvent
  | WebsocketUserReadEvent
  | WebsocketUserAttachEvent
  | WebsocketVideoEvent;

export type WebsocketEvent =
  | WebsocketNotificationEvent
  | WebsocketOnlineOfflineEvent
  | WebsocketMessagingEvent;

export function isOnlineOfflineEvent(
  event: WebsocketEvent,
): event is WebsocketOnlineOfflineEvent {
  return event.parent_type === 'onlineoffline';
}

export function isSockMessageServerEvent(
  event: WebsocketServerEvent,
): event is WebsocketMessage {
  return event.channel === 'user';
}

type BaseMessage = BaseServerData & {
  channel: 'user';
  body: {
    aggregated: null;
    display: boolean;
    id: string;
    is_important: boolean;
    is_notification: boolean;
    is_project: boolean;
    message: string;
    news_feed: boolean;
    no_persist: boolean;
    popup: boolean;
    targets: any[];
    timestamp: number;
  };
};

export type WebsocketMessage = BaseMessage & {
  body: WebsocketEvent;
};



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-i18n-webapp-compat/LANG/it/webapp/src/app/@shared/datastore/websocket/server-message-event.model.ts